<template>
  <FlotoCrudContainer
    ref="list"
    as-table
    prepend-new-item
    :scrollable="scrollable"
    :selectable="selectable"
    :fetch-fn="getAssetsTab"
    :delete-fn="deleteAssetsFn"
    :columns="tableColumns"
    :show-no-data="showNoData"
    @selection-change="$emit('selection-change', $event)"
  >
    <template
      v-if="searchable && moduleName"
      v-slot:add-controls="{ refreshList }"
    >
      <MRow class="my-4">
        <MCol>
          <FlotoSearchBar
            :allow-save-search="false"
            :persist-criteria="false"
            :allow-keywords="false"
            :module-name="moduleName"
            :addition-search-params="additionSearchParams"
            @change="applyCriteria($event, refreshList)"
          />
        </MCol>
      </MRow>
    </template>
    <template v-slot:name="{ item }">
      <td class="text-ellipsis">
        <ResourceListTitle
          :name="item.name"
          open-in-new-tab
          :internal-link="!isApproval"
          :link="
            $modules.getModuleRoute('asset', 'view', {
              params: { id: item.id, assetType: item.model },
            })
          "
        />
      </td>
    </template>
    <template v-slot:assetTypeId="{ item }">
      <td class="text-ellipsis">
        <FlotoAssetTypePicker :value="item.assetTypeId" disabled />
      </td>
    </template>
    <template v-slot:statusId="{ item }">
      <td class="text-ellipsis">
        <FlotoStatusPicker
          :value="item.statusId"
          disabled
          :module-name="$constants.ASSET"
        />
      </td>
    </template>
    <template v-slot:barcode="{ item }">
      <td class="text-ellipsis">
        {{ item.barcode ? item.barcode : '---' }}
      </td>
    </template>
    <template v-slot:remarks="{ item }">
      <td class="text-ellipsis">
        <MTooltip placement="topRight">
          <template v-slot:trigger>
            {{ item.remarks ? item.remarks : '---' }}
          </template>
          {{ item.remarks }}
        </MTooltip>
      </td>
    </template>
    <template v-slot:assetCondition="{ item }">
      <td class="text-ellipsis">
        <AssetConditionPicker
          :value="item.assetCondition"
          placeholder="---"
          disabled
        />
      </td>
    </template>
    <template v-slot:movementDate="{ item }">
      <td class="text-ellipsis">
        <span v-if="item.movementDate">
          {{ item.movementDate | datetime }}
        </span>
        <span v-else>---</span>
      </td>
    </template>
    <template v-slot:inWarranty="{ item }">
      <td class="text-ellipsis">
        {{ item.inWarranty === true ? $tc('yes') : $tc('no') }}
      </td>
    </template>
    <template v-slot:warrantyCovered="{ item }">
      <td class="text-ellipsis">
        {{ item.warrantyCovered === true ? $tc('yes') : $tc('no') }}
      </td>
    </template>
    <template v-slot:replaced="{ item }">
      <td class="text-ellipsis">
        {{ item.replaced === true ? $tc('yes') : $tc('no') }}
      </td>
    </template>
    <template v-slot:replaceBy="{ item }">
      <td class="text-ellipsis">
        <span v-if="item.replaceBy === 0">---</span>
        <span v-else>
          <AssetPickerDrawer
            v-if="item.replaceBy"
            :value="item.replaceBy"
            disabled
            placeholder="---"
            :as-input="false"
            :module-name="item.model"
            :fetch-fn="fetchAssetData"
          />
        </span>
      </td>
    </template>
    <template v-slot:repairType="{ item }">
      <td class="text-ellipsis">
        <!-- {{ item.repairType ? item.repairType : '---' }} -->
        <AssetMovementRepairTypePicker
          :value="item.repairType"
          placeholder="---"
          disabled
        />
      </td>
    </template>
    <template v-slot:barCode="{ item }">
      <td class="text-ellipsis">
        {{ item.barCode ? item.barCode : '---' }}
      </td>
    </template>
    <template v-slot:expectedDate="{ item }">
      <td class="text-ellipsis">
        <span v-if="item.expectedDate">
          {{ item.expectedDate | datetime }}</span
        >
        <span v-else>---</span>
      </td>
    </template>
    <template v-slot:referenceNumber="{ item }">
      <td class="text-ellipsis">
        {{ item.referenceNumber ? item.referenceNumber : '---' }}
      </td>
    </template>
    <template v-slot:actions="{ item, remove }">
      <td
        :class="{
          'sticky-action': actionSticked.indexOf(item.id) >= 0,
          'hover-action-container': hoverableActions,
        }"
      >
        <slot
          name="action"
          :item="item"
          :stick-action="() => stickActionFor(item)"
          :unstick-action="() => unStickActionFor(item)"
        >
          <div class="flex justify-left items-center">
            <AssetEditDrawer
              v-if="showEdit"
              :default-value="item"
              :update="handleUpdate"
              :movement-type="resource.movementType"
              :disabled="
                resource.status === 'cancelled' ||
                resource.status === 'completed'
              "
            />
            <FlotoDeleteBtn
              :message="
                $t('confirm_delete_item', {
                  item: `${$tc('asset')}`,
                })
              "
              :disabled="
                resource.status === 'cancelled' ||
                resource.status === 'completed' ||
                resource.approvalStatus === 'approved' ||
                resource.approvalStatus === 'pre_approved'
              "
              @confirm="remove"
            />
          </div>
        </slot>
      </td>
    </template>
  </FlotoCrudContainer>
</template>
<script>
import CloneDeep from 'lodash/cloneDeep'
import AssetPickerDrawer from '@components/data-picker/asset-picker-drawer'
import ResourceListTitle from '@components/resource/resource-list-title'
import {
  getAssetsApi,
  updateAssetsApi,
} from '@modules/asset-management/api/asset-movement-api'
import { transformSearchCriteriasForServer } from '@data/search'
import Uniq from 'lodash/uniq'
import AssetMovementRepairTypePicker from '@components/data-picker/asset-movement-repair-type-picker'
import AssetConditionPicker from '@components/data-picker/asset-condition-picker'
import { searchAssetsApi } from '@modules/asset/asset-api'
import { assetMovementAssetSelectionFixedSearchParams } from '@components/search/sources'
import AssetEditDrawer from './asset-edit-drawer'
export default {
  name: 'AssetsList',
  components: {
    AssetMovementRepairTypePicker,
    ResourceListTitle,
    AssetEditDrawer,
    AssetConditionPicker,
    AssetPickerDrawer,
  },
  props: {
    moduleName: { type: String, default: undefined },
    searchable: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    resourceId: { type: Number, default: undefined },
    resource: { type: Object, default: undefined },
    movement: { type: Object, default: undefined },
    fetchFn: { type: Function, default: undefined },
    hasActions: { type: Boolean, default: false },
    hoverableActions: { type: Boolean, default: false },
    showEdit: { type: Boolean, default: false },
    showNoData: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: false },
    excludedIds: {
      type: Array,
      default() {
        return []
      },
    },
    ids: {
      type: Array,
      default: undefined,
    },
    hiddenColumns: {
      type: Array,
      default() {
        return ['actions']
      },
    },
    deleteFn: { type: Function, default: undefined },
    isApproval: { type: Boolean, default: false },
  },
  data() {
    this.tableColumns = [
      {
        name: `${this.$t('id')}`,
        key: 'name',
        width: '50px',
        minWidth: '50px',
      },
      {
        name: `${this.$tc('name')}`,
        key: 'displayName',
        width: '150px',
        minWidth: '150px',
      },
      ...(this.hiddenColumns.indexOf('assetTypeId') < 0
        ? [
            {
              name: `${this.$tc('asset_type')}`,
              key: 'assetTypeId',
              width: '150px',
              minWidth: '150px',
            },
          ]
        : []),
      ...(this.hiddenColumns.indexOf('statusId') < 0
        ? [
            {
              name: `${this.$tc('status')}`,
              key: 'statusId',
              width: '50px',
              minWidth: '50px',
            },
          ]
        : []),
      ...(this.hiddenColumns.indexOf('barCode') < 0
        ? [
            {
              name: `${this.$tc('barcode')}`,
              key: 'barCode',
              width: '150px',
              minWidth: '150px',
            },
          ]
        : []),
      ...(this.hiddenColumns.indexOf('assetCondition') < 0
        ? [
            {
              name: `${this.$tc('asset')} ${this.$tc('condition')}`,
              key: 'assetCondition',
              width: '150px',
              minWidth: '150px',
            },
          ]
        : []),
      ...(this.hiddenColumns.indexOf('inWarranty') < 0
        ? [
            {
              name: `${this.$tc('in_warranty')}`,
              key: 'inWarranty',
              width: '100px',
              minWidth: '100px',
            },
          ]
        : []),
      ...(this.hiddenColumns.indexOf('remarks') < 0
        ? [
            {
              name: `${this.$tc('remark', 2)}`,
              key: 'remarks',
              width: '150px',
              minWidth: '150px',
            },
          ]
        : []),
      ...(this.resource && this.resource.movementType === 'return_from_repair'
        ? [
            {
              name: `${this.$tc('sent_date')}`,
              key: 'movementDate',
              width: '200px',
              minWidth: '200px',
            },
          ]
        : []),
      ...(this.resource &&
      this.resource.movementType === 'return_from_repair' &&
      this.hiddenColumns.indexOf('repairType') < 0
        ? [
            {
              name: `${this.$tc('repair_type')}`,
              key: 'repairType',
              width: '150px',
              minWidth: '150px',
            },
          ]
        : []),
      ...(this.resource &&
      this.resource.movementType === 'return_from_repair' &&
      this.hiddenColumns.indexOf('warrantyCovered') < 0
        ? [
            {
              name: `${this.$tc('warranty_covered')}`,
              key: 'warrantyCovered',
              width: '100px',
              minWidth: '100px',
            },
          ]
        : []),
      ...(this.resource &&
      this.resource.movementType === 'return_from_repair' &&
      this.hiddenColumns.indexOf('replaced') < 0
        ? [
            {
              name: `${this.$tc('replaced')}`,
              key: 'replaced',
              width: '100px',
              minWidth: '100px',
            },
          ]
        : []),
      ...(this.resource &&
      this.resource.movementType === 'return_from_repair' &&
      this.hiddenColumns.indexOf('replaceBy') < 0
        ? [
            {
              name: `${this.$tc('replaced_by')}`,
              key: 'replaceBy',
              width: '160px',
              minWidth: '160px',
            },
          ]
        : []),
      ...(this.hiddenColumns.indexOf('referenceNumber') < 0
        ? [
            {
              name: `${this.$tc('reference')} ${this.$tc('number')}`,
              key: 'referenceNumber',
              width: '100px',
              minWidth: '100px',
            },
          ]
        : []),
      ...(this.hiddenColumns.indexOf('expectedDate') < 0
        ? [
            {
              name: `${this.$tc('expected_return_date')}`,
              key: 'expectedDate',
              width: '100px',
              minWidth: '100px',
            },
          ]
        : []),

      ...(this.hasActions
        ? [
            {
              name: `${this.$tc('action', 2)}`,
              key: 'actions',
              width: '120px',
            },
          ]
        : []),
    ]
    return {
      isDrawerOpen: false,
      currentSelectedItemId: null,
      actionSticked: [],
      searchCriteria: this.moduleName
        ? transformSearchCriteriasForServer(this.moduleName, [])
        : {},
    }
  },
  computed: {
    additionSearchParams() {
      return assetMovementAssetSelectionFixedSearchParams().filter((source) => {
        return source.supportedIn.indexOf(this.resource.movementType) >= 0
      })
    },
  },
  methods: {
    applyCriteria(searchCriteria, refreshList) {
      this.searchCriteria = searchCriteria
      refreshList()
    },
    refresh() {
      this.$refs.list.refresh()
    },
    handleUpdate(item) {
      return updateAssetsApi(this.resource.id, item.id, item).then((data) => {
        this.refresh()
        return data
      })
    },
    getAssetsTab(limit, offset) {
      const criterias = CloneDeep(this.searchCriteria)
      let filters = {}
      if (this.excludedIds.length) {
        filters.excludedIds = this.excludedIds
      }
      if (this.ids) {
        filters.ids = this.ids
      }
      if (this.fetchFn) {
        return this.fetchFn(filters, criterias, limit, offset)
      }
      return getAssetsApi(this.resourceId)
    },
    stickActionFor(item) {
      this.actionSticked = Uniq([...this.actionSticked, item.id])
    },
    unStickActionFor(item) {
      this.actionSticked = this.actionSticked.filter((id) => id !== item.id)
    },
    deleteAssetsFn(item) {
      if (this.deleteFn) {
        return this.deleteFn(item)
      } else {
        return Promise.resolve({})
      }
    },
    fetchAssetData(moduleName, searchCriterias, limit, offset) {
      return searchAssetsApi(moduleName, searchCriterias, limit, offset)
    },
  },
}
</script>
